import DataLoader from 'dataloader'
import { delay } from 'fejl'
import { collection } from '@/infra/collection'
import { DraftJob, type DraftJobProps } from '@/features/jobs/drafting/DraftJob'
import type { JobDataLoader } from '@/features/jobs/JobDataLoader'
import {
  type Job as JobProto,
  Status as StatusProto,
  type JobUpdatedRealtimeEvent as JobUpdatedRealtimeEventProto,
} from '@taxfyle/api-internal/internal/job_pb'
import {
  ApplyCouponRequest,
  ArchiveDraftRequest,
  CreateDraftJobRequest,
  GetJobSubmissionProgressRequest,
  GetQuoteRequest,
  RemoveCouponRequest,
  ReplaceDraftQuestionAnswersRequest,
  RequestChampionProviderRequest,
  SubmitDraftJobRequest,
  UpgradeLegendRequest,
} from '@taxfyle/api-internal/internal/draft_pb'
import {
  mapJobProtoToDraftJobProps,
  mapGetQuoteResponseToQuote,
} from '@/features/jobs/drafting/draft-job-mappers'
import type { PrimeWorkspaceFn } from '@/features/iam'
import { Id } from '@/infra/id'
import type { JobsApi } from '@/features/jobs/api/job-api'
import { map, Subject } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import type { FetchJobOptions } from '@/features/jobs'
import type { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import type { AnswersMap } from '@/features/jobs/questions/questions'
import { populateAnswerMapProto } from '@/features/jobs/questions/question-mappers'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import type { Quote } from '@/features/jobs/drafting/Quote'
import { onRpcNotFoundReturnNull, retryOnRpcError } from '@/utils/grpc-util'

/**
 * Store that manages draft jobs.
 */
export class DraftJobStore {
  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this))
  private readonly _archived$ = new Subject<Id>()

  /**
   * Emits when a job is archived.
   */
  readonly archived$ = this._archived$.asObservable()

  readonly draftJobs = collection({
    create: () => new DraftJob(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  constructor(
    private readonly _api: JobsApi,
    private readonly _jobDataLoader: JobDataLoader,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {
    this.fetchDraftJob = this.fetchDraftJob.bind(this)

    // When the inner job loader clears an entry, clear it in our loader
    // as well.
    this._jobDataLoader.clear$.subscribe((id) => this._loader.clear(id))

    this._api.realtime$
      .pipe(
        map((e) => e.getJobUpdated()),
        filterNonNil(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  /**
   * Fetches a draft job by ID.
   *
   * @param id
   * @param options
   */
  async fetchDraftJob(
    id: Id,
    options?: FetchJobOptions,
  ): Promise<DraftJob | null> {
    if (!Id.some(id)) {
      return null
    }

    if (options?.force) {
      this._jobDataLoader.clear(id)
    }

    return this._loader.load(Id.assert(id))
  }

  /**
   * Fetches a quote for the job.
   *
   * @param id
   * @param signal
   */
  async fetchQuote(id: Id, signal?: AbortSignal): Promise<Quote> {
    id = Id.assert(id)

    const request = new GetQuoteRequest()
    request.setId(id)

    const response = await retryOnRpcError(
      () => this._api.draft.getQuote(request),
      { signal },
    )

    signal?.throwIfAborted()

    return mapGetQuoteResponseToQuote(response)
  }

  /**
   * Creates a new draft job.
   *
   * @param legendVersionId
   */
  async createNewDraft({ legendVersionId }: CreateNewDraftOptions) {
    const request = new CreateDraftJobRequest()
      .setLegendId(legendVersionId.id)
      .setLegendVersion(legendVersionId.version)

    const response = await this._api.draft.createDraftJob(request)
    const id = response.getId()

    // Poll for the newly created draft.
    let attempts = 0
    while (++attempts <= 20) {
      const draft = await this.fetchDraftJob(id, { force: true })
      if (draft) {
        return draft
      }
      console.warn(
        'Draft created but not yet reflected on read-side, will re-fetch soon. ID:',
        id,
      )
      await delay(500)
    }

    throw new Error('Job was created but timed out waiting to retrieve it')
  }

  /**
   * Replaces the answers with the ones specified.
   *
   * @param jobId
   * @param answers
   */
  async replaceQuestionAnswers(jobId: Id, answers: AnswersMap) {
    jobId = Id.assert(jobId)
    const request = new ReplaceDraftQuestionAnswersRequest().setId(
      Id.assert(jobId),
    )

    populateAnswerMapProto(answers, request.getAnswersMap())

    await this._api.draft.replaceDraftQuestionAnswers(request)
  }

  /**
   * Upgrades the draft job's legend version.
   *
   * @param jobId
   */
  async upgradeLegendVersion(jobId: Id) {
    jobId = Id.assert(jobId)
    const request = new UpgradeLegendRequest()
    request.setId(jobId)

    await this._api.draft.upgradeLegend(request)

    await this.fetchDraftJob(jobId, { force: true })
  }

  /**
   * Requests a champion Provider.
   *
   * @param jobId
   * @param selection
   */
  async requestChampionProvider(
    jobId: Id,
    selection: ChampionProviderSelection,
  ) {
    jobId = Id.assert(jobId)

    const request = new RequestChampionProviderRequest()
    request.setId(jobId)

    let requestedProviderUserId: Id | null = null
    if (selection.type === 'SpecificChampionProvider') {
      requestedProviderUserId = Id.assert(selection.providerUserId)
      request.setProviderUserPublicId(
        new StringValue().setValue(requestedProviderUserId),
      )
    }

    await this._api.draft.requestChampionProvider(request)

    this.draftJobs.set({ id: jobId, requestedProviderUserId })
  }

  /**
   * Applies a coupon to the job.
   *
   * @param jobId
   * @param couponId
   */
  async applyCoupon(jobId: Id, couponId: Id) {
    jobId = Id.assert(jobId)
    couponId = Id.assert(couponId)
    const request = new ApplyCouponRequest()
    request.setId(jobId)
    request.setCouponId(new StringValue().setValue(couponId))
    await this._api.draft.applyCoupon(request)
    this.draftJobs.set({ id: jobId, couponId })
  }

  /**
   * Removes a coupon from the job.
   *
   * @param jobId
   */
  async removeCoupon(jobId: Id) {
    jobId = Id.assert(jobId)
    const request = new RemoveCouponRequest()
    request.setId(jobId)
    await this._api.draft.removeCoupon(request)
    this.draftJobs.set({ id: jobId, couponId: null })
  }

  /**
   * Archives a draft job.
   */
  async archive(id: Id) {
    const request = new ArchiveDraftRequest()
    request.setId(Id.assert(id))
    await this._api.draft.archive(request)

    // Clear from the cache after archiving
    id = Id.assert(id)
    this._jobDataLoader.clear(id)
    this.draftJobs.remove(id)
    this._archived$.next(id)
  }

  /**
   * Submits the draft and returns a submission ID that can be passed
   * to {@link waitForSubmissionOutcome}.
   */
  async submit({ id, billingMethodId }: SubmitOptions) {
    const draft = this.draftJobs.get(id)
    if (!draft) {
      return
    }

    id = Id.assert(draft.id)

    const request = new SubmitDraftJobRequest()
    request.setId(id)
    if (billingMethodId) {
      request.setBillingMethodId(new StringValue().setValue(billingMethodId))
    }

    const response = await this._api.draft.submitDraftJob(request)
    const submissionId = response.getSubmissionId()

    // Clear entry but don't refetch yet.
    this._jobDataLoader.clear(id)
    return submissionId
  }

  /**
   * Waits for a job submission to finish.
   * Any submission error is thrown as {@link Error}s.
   *
   * @param submissionId
   * @param signal
   */
  async waitForSubmissionOutcome({
    submissionId,
    signal,
  }: WaitForSubmissionOutcomeOptions) {
    submissionId = Id.assert(submissionId)

    const request = new GetJobSubmissionProgressRequest()
    request.setSubmissionId(submissionId)

    // Poll for submission to finish.
    while (true) {
      signal?.throwIfAborted()

      const response = await this._api.draft
        .getJobSubmissionProgress(request)
        .catch(onRpcNotFoundReturnNull)
      if (response === null) {
        // Eventual consistency; the submission process may not be retrievable
        // yet immediately after, so wait a bit and try again.
        // It will eventually return something, even if it's already completed.
        await delay(400)
        continue
      }

      const status = response.getStatus()
      const completed = status?.getCompleted()
      if (completed) {
        // Submission completed successfully.
        // Force a re-fetch of the job before returning.
        await this.fetchDraftJob(response.getId(), { force: true })
        return
      }

      const failed = status?.getFailed()
      if (failed) {
        throw new Error(failed.getDescription())
      }

      const inProgress = status?.getInProgress()
      if (inProgress) {
        await delay(400)
        continue
      }

      console.error(
        'Unexpected status from submission progress endpoint',
        response.toObject(),
      )
      throw new Error('Unexpected status from submission progress endpoint')
    }
  }

  async _fetchManyCore(ids: readonly string[]) {
    const response = await this._jobDataLoader.fetchMany(ids)
    const props = response.map((r) =>
      r && this._isDraftProto(r) ? this._mapJobProtoToProps(r) : null,
    )

    return this.draftJobs.set(props)
  }

  private _onRemoteEvent(event: JobUpdatedRealtimeEventProto) {
    const proto = event.getJob()!

    // Only handle draft jobs
    if (!this._isDraftProto(proto)) {
      return
    }

    const props = this._mapJobProtoToProps(proto)

    this.draftJobs.set(props)
  }

  private _isDraftProto(proto: JobProto): boolean {
    return proto.getStatus() === StatusProto.UNDER_CONSTRUCTION
  }

  private _mapJobProtoToProps(proto: JobProto): DraftJobProps {
    return mapJobProtoToDraftJobProps(this._primeWorkspace, proto)
  }
}

export interface CreateNewDraftOptions {
  /**
   * Legend version to create the draft with.
   */
  legendVersionId: LegendVersionId
}

export type ChampionProviderSelection =
  | AnyChampionProvider
  | SpecificChampionProvider

/**
 * Any champion Provider.
 */
export interface AnyChampionProvider {
  type: 'AnyChampionProvider'
}

/**
 * A specific champion Provider is requested.
 */
export interface SpecificChampionProvider {
  type: 'SpecificChampionProvider'
  providerUserId: Id
}

export const ChampionProviderSelection = {
  /** @see {AnyChampionProvider} */
  anyChampionProvider: (): AnyChampionProvider => ({
    type: 'AnyChampionProvider',
  }),

  /** @see {SpecificChampionProvider} */
  specificChampionProvider: (providerUserId: Id): SpecificChampionProvider => ({
    type: 'SpecificChampionProvider',
    providerUserId,
  }),
}

/**
 * Options for submitting a job.
 */
export interface SubmitOptions {
  /**
   * ID of the job to submit.
   */
  id: Id

  /**
   * ID of the billing method to use, if any.
   */
  billingMethodId: Id | null
}

/**
 * Options for waiting for a job submission to finish.
 */
export interface WaitForSubmissionOutcomeOptions {
  /**
   * ID of the submission.
   */
  submissionId: Id

  /**
   * Signal to check between poll requests.
   */
  signal?: AbortSignal
}
